import { useEffect, useState } from 'react';
import {
  api,
  claveDeEnvio,
  duplicadoDe,
  ErrorApi,
  type Duplicado,
  type JugadorPublico,
  type Yo,
} from '../services/api';
import { Aviso, Boton, Cargando, Tarjeta, Titulo } from '../components/ui';
import { ParejaEnCarga, PasosCarga, InicialJugador, type LugarJugador } from '../components/ParejaEnCarga';
import { esEmpateDeTiebreak, esSetDeTiebreak, tiebreakValido } from '../nucleo/tiebreak';
import { DIAS_HACIA_ATRAS, diasEntre, esFechaValida, hoy, ZONA } from '../nucleo/fechas';
import { errorMarcador } from '../nucleo/marcador';

/**
 * Cargar un partido, en dos pasos: quiénes jugaron, y cómo salió.
 *
 * Son pasos y no un formulario largo porque esto se completa parado en la
 * cancha, con una mano. Las parejas permanecen visibles y la lista de jugadores tiene búsqueda.
 *
 * El borrador se guarda en el navegador: si se corta la señal o alguien cierra
 * la app a la mitad, lo tipeado no se pierde.
 */
const BORRADOR = 'ranking.borrador';

interface Borrador {
  equipoA: (number | null)[];
  equipoB: (number | null)[];
  games_a: string;
  games_b: string;
  /** Tanteo del tiebreak: opcional, solo para mostrar. */
  tb_a: string;
  tb_b: string;
  clave: string;
}

function borradorVacio(): Borrador {
  return {
    equipoA: [],
    equipoB: [],
    games_a: '',
    games_b: '',
    tb_a: '',
    tb_b: '',
    clave: claveDeEnvio(),
  };
}

function leerBorrador(): Borrador {
  try {
    const guardado = localStorage.getItem(BORRADOR);
    // El spread sobre el vacío completa los campos que un borrador viejo no
    // tenía (por ejemplo, el tanteo del tiebreak).
    if (guardado) {
      // Las versiones anteriores guardaban la fecha: nunca recuperarla.
      const { jugado_el: _fechaAnterior, ...datos } = JSON.parse(guardado);
      return { ...borradorVacio(), ...datos };
    }
  } catch {
    // Navegador con el almacenamiento bloqueado: se sigue sin borrador.
  }
  return borradorVacio();
}

export function Cargar({ yo, onCargado }: { yo: Yo; onCargado: () => void }) {
  const [jugadores, setJugadores] = useState<JugadorPublico[] | null>(null);
  const [b, setB] = useState<Borrador>(leerBorrador);
  // La excepción vale solo mientras se carga este partido, nunca en el borrador.
  const [fechaElegida, setFechaElegida] = useState<string | null>(null);
  const [hoyFecha, setHoyFecha] = useState(hoy);
  const jugadoEl = fechaElegida ?? hoyFecha;
  const [tanteoAbierto, setTanteoAbierto] = useState(() => b.tb_a !== '' || b.tb_b !== '');
  const [paso, setPaso] = useState<1 | 2>(1);
  const [lugar, setLugar] = useState<LugarJugador | null>(() => primerLugarVacio(b));
  const [busqueda, setBusqueda] = useState('');
  const [ladoMarcador, setLadoMarcador] = useState<'a' | 'b'>('a');
  const [errorJugadores, setErrorJugadores] = useState(false);
  const [reintento, setReintento] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  // El servidor encontro uno igual hoy y pregunta si es otro partido.
  const [duplicado, setDuplicado] = useState<Duplicado | null>(null);

  // Si cambia algo del partido, la pregunta ya no aplica: se vuelve a chequear
  // al mandarlo.
  const firma = JSON.stringify([jugadoEl, b.equipoA, b.equipoB, b.games_a, b.games_b]);
  useEffect(() => setDuplicado(null), [firma]);

  useEffect(() => {
    const actualizar = () => setHoyFecha(hoy());
    const reloj = window.setInterval(actualizar, 30_000);
    window.addEventListener('focus', actualizar);
    document.addEventListener('visibilitychange', actualizar);
    return () => {
      window.clearInterval(reloj);
      window.removeEventListener('focus', actualizar);
      document.removeEventListener('visibilitychange', actualizar);
    };
  }, []);

  useEffect(() => {
    let vigente = true;
    setErrorJugadores(false);
    api.paraEntrar().then(r => vigente && setJugadores(r.jugadores)).catch(() => vigente && setErrorJugadores(true));
    return () => { vigente = false; };
  }, [reintento]);

  useEffect(() => {
    try {
      localStorage.setItem(BORRADOR, JSON.stringify(b));
    } catch {
      // Sin almacenamiento: no es grave, solo se pierde el borrador.
    }
  }, [b]);

  if (errorJugadores) return <div className="flex flex-col gap-3"><Aviso tono="error">No pudimos traer los jugadores.</Aviso><Boton onClick={() => setReintento(n => n + 1)}>Volver a intentar</Boton></div>;
  if (!jugadores) return <Cargando que="Buscando jugadores" />;

  const elegidos = [...b.equipoA, ...b.equipoB].filter((id): id is number => id !== null);
  const listo = b.equipoA.length === 2 && b.equipoB.length === 2 && elegidos.length === 4
    && new Set(elegidos).size === 4 && elegidos.every(id => jugadores.some(j => j.id === id));
  const minimaFecha = new Date(Date.parse(`${hoyFecha}T00:00:00Z`) - DIAS_HACIA_ATRAS * 86_400_000).toISOString().slice(0, 10);
  const fechaValida = esFechaValida(jugadoEl) && diasEntre(jugadoEl, hoyFecha) >= 0
    && diasEntre(jugadoEl, hoyFecha) <= DIAS_HACIA_ATRAS;
  const fechaTexto = fechaValida ? `${jugadoEl === hoyFecha ? 'Hoy · ' : ''}${new Date(`${jugadoEl}T15:00:00Z`).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: ZONA })}` : 'Elegí la fecha del partido';

  function cambiarLugar(destino: LugarJugador, id: number | null) {
    const campo = destino.lado === 'a' ? 'equipoA' : 'equipoB';
    if (id !== null && b[campo][destino.indice] === id) {
      setLugar(primerLugarVacio(b)); setBusqueda(''); return;
    }
    if (id !== null && elegidos.includes(id) && b[destino.lado === 'a' ? 'equipoA' : 'equipoB'][destino.indice] !== id) return;
    const equipo = [b[campo][0] ?? null, b[campo][1] ?? null];
    equipo[destino.indice] = id;
    const actual = { ...b, [campo]: equipo, games_a: '', games_b: '', tb_a: '', tb_b: '' };
    setB(actual); setError(null); setBusqueda('');
    setLugar(id === null ? destino : primerLugarVacio(actual));
  }

  function descartarBorrador() {
    try {
      localStorage.removeItem(BORRADOR);
    } catch {
      /* nada */
    }
    setB(borradorVacio());
    setFechaElegida(null);
    setHoyFecha(hoy());
    setLugar({ lado: 'a', indice: 0 }); setBusqueda(''); setLadoMarcador('a');
    setPaso(1);
    setDuplicado(null);
  }

  async function enviar(confirmarDuplicado = false) {
    if (ocupado || !listo || !fechaValida || b.games_a === '' || b.games_b === '' || errorTanteo
      || errorMarcador(Number(b.games_a), Number(b.games_b))) return;
    const ga = Number(b.games_a);
    const gb = Number(b.games_b);
    // El tanteo solo viaja si el set es de tiebreak y está completo de los dos
    // lados. A medias, el botón ni se habilita (ver `faltaTanteo`).
    const conTanteo = esSetDeTiebreak(ga, gb) && b.tb_a !== '' && b.tb_b !== '';

    setOcupado(true);
    setError(null);
    try {
      await api.cargarPartido({
        jugado_el: fechaElegida ?? hoy(),
        equipoA: [b.equipoA[0]!, b.equipoA[1]!],
        equipoB: [b.equipoB[0]!, b.equipoB[1]!],
        games_a: ga,
        games_b: gb,
        tb_a: conTanteo ? Number(b.tb_a) : null,
        tb_b: conTanteo ? Number(b.tb_b) : null,
        clave_idem: b.clave,
        confirmar_duplicado: confirmarDuplicado,
      });
      descartarBorrador();
      onCargado();
    } catch (e) {
      const igual = duplicadoDe(e);
      if (igual) setDuplicado(igual);
      else setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar.');
    } finally {
      setOcupado(false);
    }
  }

  // ── Paso 1: cada jugador ocupa un lugar explícito de su pareja ──
  if (paso === 1) {
    const personas = (equipo: (number | null)[]) => ([0, 1] as const).map(i =>
      jugadores.find(j => j.id === equipo[i]) ?? null);
    const destino = lugar;
    const actual = destino ? b[destino.lado === 'a' ? 'equipoA' : 'equipoB'][destino.indice] : null;
    const disponibles = jugadores.filter(j => (!elegidos.includes(j.id) || j.id === actual)
      && normalizar(j.nombre).includes(normalizar(busqueda)));
    return <div className="flex flex-col gap-3">
      <Titulo grande>¿Quiénes jugaron?</Titulo>
      <PasosCarga paso={1} />
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold">Fecha del partido: {fechaTexto}</p>
        {fechaElegida === null ? <button type="button" onClick={() => setFechaElegida(hoy())}
          className="min-h-11 self-start text-sm font-semibold text-(--color-acento) underline">
          Cambiar fecha solo para este partido
        </button> : <>
          <label className="flex min-w-0 items-center justify-between gap-3 text-sm">
            <span>Otra fecha</span>
            <input type="date" aria-label="Fecha del partido" value={fechaElegida} min={minimaFecha} max={hoyFecha}
              onChange={e => { setFechaElegida(e.target.value); setError(null); }}
              className="min-h-11 min-w-0 rounded-xl border border-(--color-borde) bg-(--color-panel) px-2 text-base [color-scheme:dark]" />
          </label>
          <p className="text-xs text-(--color-apagado)">Solo para este partido. Podés cargar hasta {DIAS_HACIA_ATRAS} días atrás. Si el mes ya cerró, lo carga un admin.</p>
          <button type="button" onClick={() => { setFechaElegida(null); setError(null); }}
            className="min-h-11 self-start text-sm font-semibold text-(--color-acento) underline">Usar hoy</button>
        </>}
        {!fechaValida && <p role="status" className="text-sm text-(--color-alerta)">Elegí una fecha entre {minimaFecha} y {hoyFecha}.</p>}
      </div>
      {destino && <section aria-label="Elegir jugador" className="flex flex-col gap-2">
        <p className="text-sm font-semibold">Elegí {destino.indice === 0 ? 'el primer' : 'el segundo'} jugador de Pareja {destino.lado === 'a' ? 1 : 2}</p>
        <input type="search" aria-label="Buscar jugador" placeholder="Buscar jugador" value={busqueda}
          onChange={e => setBusqueda(e.target.value)} className="min-h-11 rounded-xl border border-(--color-borde)
          bg-(--color-panel) px-3 text-base outline-none focus:border-(--color-acento)" />
        <div className="grid max-h-40 grid-cols-2 gap-2 overflow-y-auto overscroll-contain" tabIndex={0} aria-label="Jugadores disponibles">
          {disponibles.map(j => <button key={j.id} type="button" onClick={() => cambiarLugar(destino, j.id)}
            aria-label={`Elegir a ${j.nombre}`} aria-pressed={j.id === actual}
            className="flex min-h-12 items-center gap-2 rounded-xl border border-(--color-borde) bg-(--color-panel) px-2 py-1 text-left active:bg-white/10">
            <InicialJugador nombre={j.nombre} /><span className="min-w-0 break-words text-sm">{j.nombre}{j.id === yo.jugador.id && <span className="ml-1 text-xs text-(--color-acento)">vos</span>}</span>
          </button>)}
          {disponibles.length === 0 && <p className="col-span-2 py-2 text-sm text-(--color-apagado)">No encontramos jugadores con ese nombre.</p>}
        </div>
      </section>}
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-1.5">
      <ParejaEnCarga numero={1} lado="a" personas={personas(b.equipoA)} lugar={lugar}
        elegir={l => { setLugar(l); setBusqueda(''); }} quitar={l => cambiarLugar(l, null)} yo={yo.jugador.id} />
      <p aria-hidden="true" className="self-center font-titulo text-sm text-(--color-apagado)">VS</p>
      <ParejaEnCarga numero={2} lado="b" personas={personas(b.equipoB)} lugar={lugar}
        elegir={l => { setLugar(l); setBusqueda(''); }} quitar={l => cambiarLugar(l, null)} yo={yo.jugador.id} />
      </div>
      {listo && <p className="text-center text-sm text-(--color-apagado)">Revisá las parejas. Tocá un nombre para cambiarlo.</p>}
      <Boton ancho deshabilitado={!listo || !fechaValida} onClick={() => { setPaso(2); setLugar(null); setLadoMarcador(b.games_a === '' ? 'a' : 'b'); }}>
        {!fechaValida ? 'Revisá la fecha' : listo ? 'Seguir al resultado →' : elegidos.length === 4 ? 'Revisá los jugadores' : `Falta${4 - elegidos.length === 1 ? '' : 'n'} ${4 - elegidos.length} jugador${4 - elegidos.length === 1 ? '' : 'es'}`}
      </Boton>
      <p className="text-center text-xs text-(--color-apagado)">Borrador guardado en este celular</p>
    </div>;
  }

  // ── Paso 2: el marcador ──
  const nombre = (id: number | null) => jugadores.find((j) => j.id === id)?.nombre ?? '?';
  const nombresA = b.equipoA.map(nombre).join(' · ');
  const nombresB = b.equipoB.map(nombre).join(' · ');

  const ga = b.games_a === '' ? null : Number(b.games_a);
  const gb = b.games_b === '' ? null : Number(b.games_b);

  // 5-5 o 6-6: el grupo lo define con un tiebreak. La app pregunta quién lo
  // ganó en vez de dejar el botón gris sin explicar por qué.
  const empateDeTiebreak = ga !== null && ga === gb && esEmpateDeTiebreak(ga);
  const empateSinSalida = ga !== null && ga === gb && !empateDeTiebreak;
  const setDeTiebreak = ga !== null && gb !== null && esSetDeTiebreak(ga, gb);
  const faltaTanteo = setDeTiebreak && (b.tb_a === '') !== (b.tb_b === '');
  const tanteo = setDeTiebreak ? tiebreakValido(ga!, gb!,
    b.tb_a === '' ? null : Number(b.tb_a), b.tb_b === '' ? null : Number(b.tb_b)) : null;
  const contradiceGanador = setDeTiebreak && !faltaTanteo && b.tb_a !== ''
    && Number(b.tb_a) !== Number(b.tb_b) && (ga! > gb!) !== (Number(b.tb_a) > Number(b.tb_b));
  const errorTanteo = tanteo && !tanteo.ok ? faltaTanteo ? 'Completá el tanteo de las dos parejas, o dejá ambos campos vacíos.'
    : contradiceGanador ? `Elegiste ganadores a ${ga! > gb! ? nombresA : nombresB}, pero el tiebreak ${b.tb_a}–${b.tb_b} indica que ganaron ${Number(b.tb_a) > Number(b.tb_b) ? nombresA : nombresB}. Revisá el ganador o el tanteo.`
      : tanteo.error : null;

  const errorSet = ga !== null && gb !== null ? errorMarcador(ga, gb) : null;
  const puedeEnviar = listo && fechaValida && ga !== null && gb !== null && !errorSet && !errorTanteo;

  let motivo = '';
  if (ga === null || gb === null) motivo = 'Marcá los games de las dos parejas';
  else if (empateDeTiebreak) motivo = 'Elegí quién ganó el tiebreak';
  else if (empateSinSalida) motivo = 'No puede quedar empatado';
  else if (faltaTanteo) motivo = 'Completá el tiebreak de los dos lados';
  else if (errorTanteo) motivo = 'Revisá el tanteo del tiebreak';
  else if (errorSet) motivo = 'Revisá el resultado';
  else if (!fechaValida) motivo = 'Revisá la fecha';

  // Cambiar los games borra el tanteo: si el set dejó de ser de tiebreak, un
  // tanteo viejo quedaría colgado y el servidor rechazaría el partido.
  const ponerGames = (lado: 'a' | 'b', v: string) =>
    setB((actual) =>
      lado === 'a'
        ? { ...actual, games_a: v, tb_a: '', tb_b: '' }
        : { ...actual, games_b: v, tb_a: '', tb_b: '' },
    );

  // 5-5 + tiebreak para la pareja 1 -> 6-5. El tiebreak vale como el game que
  // define el set, que es como se anota siempre.
  const ganoElTiebreak = (lado: 'a' | 'b') =>
    setB((actual) => {
      const empate = Math.min(Number(actual.games_a), Number(actual.games_b));
      return {
        ...actual,
        games_a: String(lado === 'a' ? empate + 1 : empate),
        games_b: String(lado === 'b' ? empate + 1 : empate),
        tb_a: '',
        tb_b: '',
      };
    });

  return (
    <fieldset disabled={ocupado} className="flex min-w-0 flex-col gap-3">
      <Titulo grande>¿Cómo salió?</Titulo>

      <PasosCarga paso={2} />
      <p className="text-xs text-(--color-apagado)">{fechaTexto} · La fecha se cambia en Jugadores.</p>
      <Tarjeta className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
        {(['a', 'b'] as const).map((lado, i) => <div key={lado} className={`flex flex-col gap-2 ${i === 1 ? 'col-start-3' : ''}`}>
          <p className="text-center text-xs uppercase text-(--color-apagado)">Pareja {i + 1}</p>
          <p className="min-h-12 break-words text-center text-sm font-semibold">{(lado === 'a' ? b.equipoA : b.equipoB).map(id => <span key={id} className="block">{nombre(id)}</span>)}</p>
          <button type="button" aria-label={`Cambiar games de Pareja ${i + 1}`} aria-pressed={ladoMarcador === lado}
            onClick={() => setLadoMarcador(lado)}
            className={`min-h-24 rounded-xl border-2 font-titulo text-6xl tabular-nums ${ladoMarcador === lado
              ? 'border-(--color-acento) bg-(--color-acento)/10 text-(--color-acento)' : 'border-(--color-borde) bg-(--color-panel)'}`}>
            {(lado === 'a' ? b.games_a : b.games_b) || '–'}
          </button>
        </div>)}
        <span aria-hidden="true" className="col-start-2 row-start-1 pb-6 text-2xl text-(--color-apagado)">–</span>
      </Tarjeta>
      <div className="flex flex-col gap-2">
        <p className="text-sm font-semibold">Games de {ladoMarcador === 'a' ? nombresA : nombresB}</p>
        <LadoMarcador valor={ladoMarcador === 'a' ? b.games_a : b.games_b} onCambio={v => {
          ponerGames(ladoMarcador, v);
          if (ladoMarcador === 'a' && b.games_b === '') setLadoMarcador('b');
        }} />
        <p className="text-xs text-(--color-apagado)">Tocá el marcador de la pareja que querés cambiar.</p>
      </div>

      <p className="text-xs text-(--color-apagado)">Si se terminó la hora, podés cargar un set corto: por ejemplo, 4–2 o 5–3. El ganador debe llegar al menos a 4 games y no puede haber empate.</p>
      {errorSet && !empateDeTiebreak && <Aviso tono="alerta">{errorSet}</Aviso>}

      {(empateDeTiebreak || setDeTiebreak) && ga !== null && gb !== null && (
        <Tarjeta compacta className="flex flex-col gap-2 border-(--color-acento)/50">
          <h3 className="font-titulo text-xl">{empateDeTiebreak ? `${ga}–${gb}: ¿quién ganó el tiebreak?` : 'Se definió en tiebreak'}</h3>
          <div className="grid grid-cols-2 gap-2">
            {(['a', 'b'] as const).map((lado, i) => {
              const gano = !empateDeTiebreak && (lado === 'a' ? ga > gb : gb > ga);
              return <button key={lado} type="button" aria-label={`Ganó el tiebreak Pareja ${i + 1}`}
                aria-pressed={gano} onClick={() => ganoElTiebreak(lado)}
                className={`min-h-12 rounded-xl border px-2 py-2 text-sm font-semibold ${gano
                  ? 'border-(--color-acento) bg-(--color-acento) text-(--color-sobre-acento)'
                  : 'border-(--color-borde) bg-(--color-panel)'}`}>
                {gano && <span aria-hidden="true">✓ </span>}{lado === 'a' ? nombresA : nombresB}
              </button>;
            })}
          </div>
          <p className="text-xs text-(--color-apagado)">El tiebreak define el set: se anota {Math.min(ga, gb) + 1}–{Math.min(ga, gb)} para la pareja ganadora.</p>
        </Tarjeta>
      )}

      {setDeTiebreak && (
        <Tarjeta>
          <details open={tanteoAbierto} onToggle={(e) => setTanteoAbierto(e.currentTarget.open)}>
            <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3
              text-base font-semibold [&::-webkit-details-marker]:hidden">
              <span>
                Anotar tiebreak
                <span className="block text-sm font-normal text-(--color-apagado)">Opcional</span>
              </span>
              <span aria-hidden className="text-sm font-normal text-(--color-apagado)">
                {tanteoAbierto ? '−' : '+'}
              </span>
            </summary>
            <div className="mt-3 flex flex-col gap-2">
              <Tanteo
                nombres={nombresA}
                valor={b.tb_a}
                onCambio={(v) => setB((a) => ({ ...a, tb_a: v }))}
                color="text-(--color-acento)"
              />
              <Tanteo
                nombres={nombresB}
                valor={b.tb_b}
                onCambio={(v) => setB((a) => ({ ...a, tb_b: v }))}
                color="text-sky-400"
              />
            </div>
            <p className="mt-3 text-sm leading-normal">
              Ejemplo: 7-5. Se ve en el historial y no cambia el ranking.
            </p>
          </details>
          <p role="status" aria-live="polite" className="mt-2 text-sm font-semibold text-(--color-alerta)">{errorTanteo}</p>
        </Tarjeta>
      )}

      {puedeEnviar && <Tarjeta compacta className="border-(--color-acento)/50">
        <p className="font-semibold">Ganaron {ga! > gb! ? nombresA : nombresB}</p>
        <p className="text-sm text-(--color-apagado)">{ga}–{gb} · {fechaTexto}</p>
      </Tarjeta>}
      {error && <Aviso tono="error" legible>{error}</Aviso>}

      <p className="text-xs leading-normal text-(--color-apagado)">
        Ya cuenta en la tabla. El rival revisa el resultado; si está mal, puede rechazarlo.
      </p>

      {duplicado && ga !== null && gb !== null ? (
        // Lo mas probable es que sea el mismo, cargado desde otro celular: por
        // eso "es el mismo" es el boton principal, y cargarlo igual pide
        // decirlo con todas las letras.
        <Tarjeta className="border-2 border-(--color-alerta)">
          <p className="font-semibold text-(--color-alerta)">
            Ya hay un partido igual cargado para esta fecha
          </p>
          <p className="mt-2 text-base leading-normal">
            Mismas parejas y mismo resultado ({ga}-{gb}).{' '}
            {duplicado.cargado_por === yo.jugador.nombre
              ? 'Lo cargaste vos'
              : `Lo cargó ${duplicado.cargado_por}`}{' '}
            a las {horaDe(duplicado.creado_en)}
            {duplicado.estado === 'confirmado'
              ? ', y ya está confirmado.'
              : ', y está esperando que lo confirmen.'}
          </p>
          <p className="mt-2 text-base font-semibold">¿Es un partido nuevo?</p>
          <div className="mt-4 flex flex-col gap-2">
            <Boton
              ancho
              onClick={() => {
                descartarBorrador();
                onCargado();
              }}
            >
              No, es el mismo: no cargarlo
            </Boton>
            <Boton
              ancho
              tipo="secundario"
              deshabilitado={ocupado}
              onClick={() => enviar(true)}
            >
              {ocupado ? 'Cargando...' : 'Sí, es otro partido: cargarlo'}
            </Boton>
          </div>
        </Tarjeta>
      ) : (
        <div className="flex gap-2">
          <Boton tipo="secundario" onClick={() => { setPaso(1); setLugar(null); }}>
            Atrás
          </Boton>
          <Boton ancho deshabilitado={!puedeEnviar || ocupado} onClick={() => enviar()}>
            {ocupado ? 'Cargando...' : puedeEnviar ? 'Cargar partido' : motivo}
          </Boton>
        </div>
      )}
    </fieldset>
  );
}

function LadoMarcador({ valor, onCambio }: { valor: string; onCambio: (v: string) => void }) {
  return <div role="group" aria-label="Elegir games" className="grid grid-cols-4 gap-2">
    {[0, 1, 2, 3, 4, 5, 6, 7].map(n => <button key={n} type="button" onClick={() => onCambio(String(n))}
      aria-pressed={valor === String(n)} className={`min-h-12 rounded-xl border text-xl tabular-nums
        ${valor === String(n) ? 'border-(--color-acento) bg-(--color-acento) font-bold text-(--color-sobre-acento)'
          : 'border-(--color-borde) bg-(--color-panel)'}`}>
      {n}
    </button>)}
  </div>;
}

function primerLugarVacio(b: Borrador): LugarJugador | null {
  for (const lado of ['a', 'b'] as const) for (const indice of [0, 1] as const)
    if (!b[lado === 'a' ? 'equipoA' : 'equipoB'][indice]) return { lado, indice };
  return null;
}
function normalizar(texto: string) { return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim(); }

function Tanteo({
  nombres,
  valor,
  onCambio,
  color,
}: {
  nombres: string;
  valor: string;
  onCambio: (v: string) => void;
  color: string;
}) {
  return (
    <label className="flex items-center gap-3">
      <span className={`min-w-0 flex-1 truncate text-base font-semibold ${color}`}>{nombres}</span>
      <input
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        value={valor}
        onChange={(e) => onCambio(e.target.value.replace(/\D/g, '').slice(0, 2))}
        className="h-11 w-16 rounded-lg border border-(--color-borde) bg-(--color-fondo)
          text-center text-lg tabular-nums outline-none focus:border-(--color-acento)"
      />
    </label>
  );
}

/** 1758330900 -> '22:15', en la hora del celular. */
function horaDe(segundos: number): string {
  return new Date(segundos * 1000).toLocaleTimeString('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23', // es-AR solo pondria "10:35 p. m."
  });
}

